import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useStore } from 'react-redux';
import NiceModal from '@ebay/nice-modal-react';
import { getLevelNodeEnrollment } from 'services/api';
import { getProfileInfo } from 'services/utils';
import ModalEnrollmentClosure from 'pctComponents/modals/ModalEnrollmentClosure';

const CLOSED_STATUSES = ['COMPLETED', 'PASSED', 'FAILED'];
const SCORM_COMMIT_TIMEOUT = 3000;

const getIdEnrollmentFromPath = (pathname: string): string | null => {
    const parts = pathname.split('/').filter(Boolean);
    const route = parts[0];

    switch (route) {
        case 'lms':
        case 'lessons':
        case 'evaluations':
            return parts[1] || null;
        case 'contentNode':
            return parts[3] || null;
        default:
            return null;
    }
};

const useEnrollmentClosureModal = () => {
    const history = useHistory();
    const location = useLocation();
    const store = useStore();

    const bypassRef = useRef(false);
    const checkingRef = useRef(false);

    useEffect(() => {
        const idEnrollment = getIdEnrollmentFromPath(location.pathname);

        if (!idEnrollment) {
            return;
        }

        const shownKey = `enrollmentClosureShown_${idEnrollment}`;
        if (localStorage.getItem(shownKey)) {
            return;
        }

        const idUser = getProfileInfo()?.idUser;
        if (!idUser) {
            return;
        }

        const baselineKey = `enrollmentClosureBaseline_${idEnrollment}`;

        if (!sessionStorage.getItem(baselineKey)) {
            const state: any = store.getState();
            const cached = state?.apiReplicationState?.getLevelNodeEnrollment?.data?.node?.nodeEnrollment?.enrollment;

            if (cached?.status && String(cached.idEnrollment) === String(idEnrollment)) {
                sessionStorage.setItem(baselineKey, cached.status);
            }
        }

        const unblock = history.block((nextLocation) => {
            if (bypassRef.current || checkingRef.current) {
                return undefined;
            }

            const target = `${nextLocation.pathname}${nextLocation.search || ''}`;
            checkingRef.current = true;

            const proceed = () => {
                bypassRef.current = true;
                unblock();
                history.push(target);
            };

            const checkEnrollmentStatus = () => {
                getLevelNodeEnrollment(idUser, String(idEnrollment), null)
                    .then((response) => {
                        const nodeData = response?.data?.node;
                        const nodeEnrollment = nodeData?.nodeEnrollment;
                        const enrollment = nodeEnrollment?.enrollment;
                        const status = enrollment?.status;
                        const baselineStatus = sessionStorage.getItem(baselineKey);

                        const hasClosed = CLOSED_STATUSES.includes(status);
                        const wasAlreadyClosed = baselineStatus ? CLOSED_STATUSES.includes(baselineStatus) : false;

                        if (!hasClosed) {
                            if (status) {
                                sessionStorage.setItem(baselineKey, status);
                            }
                            proceed();
                            return;
                        }

                        if (wasAlreadyClosed) {
                            proceed();
                            return;
                        }

                        const team = nodeEnrollment?.team;
                        const teamExpired = team?.finalDate ? Date.now() > new Date(team.finalDate).getTime() : false;
                        const allowTeamReenrollment = Boolean(team?.allowReenrollment) && !teamExpired;
                        const hasTeamAvailable = Boolean(nodeData?.hasTeamAvailable);
                        const selfEnrollment = Boolean(nodeData?.selfEnrollment);
                        const canReenroll = allowTeamReenrollment || hasTeamAvailable || selfEnrollment;

                        localStorage.setItem(shownKey, 'true');

                        NiceModal.show(ModalEnrollmentClosure, {
                            obj: {
                                variant: status,
                                trainingName: nodeData?.title || '',
                                finalGrade: enrollment?.grade ?? nodeEnrollment?.grade ?? null,
                                masteryScore: nodeData?.masteryScore ?? null,
                                idUser,
                                idEnrollment: enrollment?.idEnrollment ?? idEnrollment,
                                idNodeEnrollment: nodeEnrollment?.idNodeEnrollment,
                                hasCertificate: Boolean(team?.idCertificate),
                                canReenroll,
                                nodeCode: nodeData?.nodeCode,
                                idTeam: team?.idTeam,
                                allowTeamReenrollment,
                                hasTeamAvailable,
                                selfEnrollment,
                                nodeData,
                            },
                            onClose: proceed,
                        });
                    })
                    .catch(() => proceed());
            };

            const scormCloseFn = (store.getState() as any)?.configHeaderFooterState?.configHeaderFooter?.closeFn;

            if (!scormCloseFn) {
                checkEnrollmentStatus();
                return false;
            }

            let commitTimeout: ReturnType<typeof setTimeout>;

            const onScormCommit = (event: MessageEvent) => {
                if (event.data !== 'callLmsFinishCallback') {
                    return;
                }
                window.removeEventListener('message', onScormCommit);
                clearTimeout(commitTimeout);
                checkEnrollmentStatus();
            };

            window.addEventListener('message', onScormCommit);
            commitTimeout = setTimeout(() => {
                window.removeEventListener('message', onScormCommit);
                checkEnrollmentStatus();
            }, SCORM_COMMIT_TIMEOUT);

            scormCloseFn(false);

            return false;
        });

        return () => {
            unblock();
            bypassRef.current = false;
            checkingRef.current = false;
        };
    }, [location.pathname, history, store]);
};

export default useEnrollmentClosureModal;
